import { Dayjs } from 'dayjs'
import { GetAllInboundsReq } from '@/services/inbound/allInboundStatus/types'
import { ALL_SERVICE_TYPES_VALUE, FIELD_NAMES, LOGISTICS_SERVICE_TYPE_OPTIONS } from './constants'

/** FilterArea 날짜 필드 값 — 단일/기간 어느 쪽으로 오든 받는다. */
type DateValue = Dayjs | [Dayjs | null, Dayjs | null] | null | undefined

export interface AllInboundStatusFormValues {
  /** 화주유형/화주명 멀티셀렉트 두 칸 — 둘 다 배열이다. '전체' 는 빈 문자열 항목으로 온다. */
  [FIELD_NAMES.logisticsServiceTypes]?: string[]
  [FIELD_NAMES.shipperNames]?: string[]
  /** 입고번호 다건 입력 값 — 배열로 오지만 단건 문자열로 올 때도 받는다. */
  [FIELD_NAMES.ordOptionNumbers]?: string[] | string
  [FIELD_NAMES.plnDateRange]?: DateValue
  [FIELD_NAMES.actDate]?: DateValue
  [FIELD_NAMES.orderType]?: string
  [FIELD_NAMES.orderStatus]?: string
}

const DATE_FORMAT = 'YYYYMMDD'

const toDateString = (value?: Dayjs | null): string | undefined => {
  if (!value || !value.isValid()) return undefined
  return value.format(DATE_FORMAT)
}

/** 기간 필드에서 [시작, 끝] 을 꺼낸다. 단일 값으로 오면 시작·끝을 같은 날로 본다. */
const toRange = (value: DateValue): [Dayjs | null, Dayjs | null] => {
  if (!value) return [null, null]
  if (Array.isArray(value)) return [value[0] ?? null, value[1] ?? null]
  return [value, value]
}

/** 단일 날짜 필드에서 한 날을 꺼낸다. 기간으로 그려져 값이 배열로 와도 시작일을 쓴다. */
const toSingleDate = (value: DateValue): Dayjs | null => {
  if (!value) return null
  return Array.isArray(value) ? value[0] ?? null : value
}

/** 문자열 배열/단건 문자열 어느 쪽으로 와도 목록으로 맞춘다. */
const toTrimmedList = (values?: string[] | string): string[] => {
  if (Array.isArray(values)) return values.map((v) => String(v).trim()).filter(Boolean)
  if (typeof values === 'string') return values.trim() ? [values.trim()] : []
  return []
}

/**
 * 화주유형 '전체' → 실제 6종으로 펼친다(개발 결정, main 동일).
 *
 * 빈 배열로 보내면 화주명이 비어 있을 때 BE 필수 필터(화주명 OR 화주유형)에 걸려 400 이 난다.
 * `serviceType IN (6종)` 은 전 화주유형과 결과가 같으면서 필수 필터도 충족한다.
 * '전체' 는 산출물 멀티셀렉트(`allOption`)가 빈 문자열 항목으로 올린다 — `toTrimmedList` 전에 판정한다.
 */
const expandAllServiceTypes = (raw: unknown): string[] => {
  const list = Array.isArray(raw) ? raw.map((v) => String(v)) : typeof raw === 'string' ? [raw] : []
  return list.includes(ALL_SERVICE_TYPES_VALUE) ? LOGISTICS_SERVICE_TYPE_OPTIONS.map((o) => o.value) : toTrimmedList(list)
}

/**
 * 필터 폼 값 → 조회 요청 변환.
 *
 * 빈 값은 싣지 않는다 — 대부분의 필터가 EQUAL 이라 빈 문자열이 그대로 가면 무결과가 된다(1.0 getFilterDict 동치).
 * 날짜는 `yyyyMMdd` 문자열로 보낸다(BE 가 이 포맷만 파싱하고, 실패 시 400).
 *
 * 화주유형의 '전체' 는 6종으로 펼쳐 싣는다(`expandAllServiceTypes`). 미선택이면 키를 싣지 않는다 = 전 화주유형.
 */
export const toAllInboundsReq = (values: AllInboundStatusFormValues): Omit<GetAllInboundsReq, 'page' | 'size'> => {
  const logisticsServiceTypes = expandAllServiceTypes(values[FIELD_NAMES.logisticsServiceTypes])
  const shipperNames = toTrimmedList(values[FIELD_NAMES.shipperNames])
  const ordOptionNumbers = toTrimmedList(values[FIELD_NAMES.ordOptionNumbers])
  const [plnFrom, plnTo] = toRange(values[FIELD_NAMES.plnDateRange])
  const plnDateFrom = toDateString(plnFrom)
  const plnDateTo = toDateString(plnTo)
  const actDate = toDateString(toSingleDate(values[FIELD_NAMES.actDate]))

  return {
    ...(shipperNames.length > 0 ? { shipperNames } : {}),
    ...(logisticsServiceTypes.length > 0 ? { logisticsServiceTypes } : {}),
    ...(ordOptionNumbers.length > 0 ? { ordOptionNumbers } : {}),
    ...(values[FIELD_NAMES.orderType] ? { orderType: values[FIELD_NAMES.orderType] } : {}),
    ...(values[FIELD_NAMES.orderStatus] ? { orderStatus: values[FIELD_NAMES.orderStatus] } : {}),
    ...(plnDateFrom ? { plnDateFrom } : {}),
    ...(plnDateTo ? { plnDateTo } : {}),
    ...(actDate ? { actDate } : {}),
  }
}

/**
 * 필수 필터 검증 — 화주명 OR 화주유형.
 *
 * BE `validateRequiredFilter` 와 같은 조건이다. 서버 400 을 그대로 맞아도 동작은 같지만,
 * ST11 과 달리 이 화면은 화주명 기본값이 비어 있을 수 있어(운영 계정) 실사용에서 자주 걸린다 — 선차단한다.
 */
export const hasRequiredFilter = (req: Omit<GetAllInboundsReq, 'page' | 'size'>): boolean =>
  (req.shipperNames?.length ?? 0) > 0 || (req.logisticsServiceTypes?.length ?? 0) > 0

/**
 * 다운로드 사유 모달 입력 → 감사용 사유 문자열. 1.0 RemarkPop REMARK(`{사유코드}_{상세사유}`) 동치 —
 * BE AccessAuditAspect 가 `detailedReason` 요청 파라미터를 EXPORT 감사로그 remark 에 그대로 남긴다.
 */
export const toDetailedReason = ({ reasonCode, reasonDetail }: { reasonCode: string; reasonDetail: string }): string =>
  `${reasonCode}_${reasonDetail}`
